/** Share-link and invitation tokens: base64url, carried in the URL fragment only (spec §9.3). */
export const TOKEN_RE = /^[A-Za-z0-9_-]{20,128}$/;

export type TokenKind = 'share' | 'invite';
const KEY: Record<TokenKind, string> = { share: 'os.pending.share', invite: 'os.pending.invite' };

/** Reads the fragment token and strips it from the address bar first; null when absent or malformed. */
export function takeFragmentToken(): string | null {
  const raw = window.location.hash.slice(1);
  if (!raw) return null;
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
  return TOKEN_RE.test(raw) ? raw : null;
}

/** Keeps a token for this tab only (sign-in hop, React StrictMode's double render); never in a URL. */
export function stashToken(kind: TokenKind, token: string): void {
  try {
    sessionStorage.setItem(KEY[kind], token);
  } catch {
    // storage blocked: the person opens the link again after signing in
  }
}

function peekStashedToken(kind: TokenKind): string | null {
  try {
    const t = sessionStorage.getItem(KEY[kind]);
    return t && TOKEN_RE.test(t) ? t : null;
  } catch {
    return null;
  }
}

/** Destructive read: the token is forgotten once used. */
export function takeStashedToken(kind: TokenKind): string | null {
  const t = peekStashedToken(kind);
  try {
    sessionStorage.removeItem(KEY[kind]);
  } catch {
    // nothing stored
  }
  return t;
}

/** The token of the page: from the fragment (then stashed) or from an earlier visit of this tab. */
export function pendingToken(kind: TokenKind): string | null {
  const fromUrl = takeFragmentToken();
  if (fromUrl) {
    stashToken(kind, fromUrl);
    return fromUrl;
  }
  return peekStashedToken(kind);
}
